import { useState, useEffect } from 'react';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import { Tooltip, Grid, makeStyles } from '@material-ui/core';
import LineChartIcon from '@material-ui/icons/ShowChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import { t, Trans } from '@lingui/macro';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { metricDefFormatter } from '../../../utilities';

import Panel from '../../page-framework/panel/PanelDecorator';
import { SessionStorageUtilities } from '../../../utilities/StorageUtilities';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { useQueryLoader } from '../../data-table/Loaders';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import UiPerformanceBoxPlot from './UiPerformanceBoxPlot';
import UiPerformanceChart from './UiPerformanceChart';

const TAB_STATES = { LINE: 'line', BAR: 'bar' };
const defaultView = TAB_STATES.LINE;

const dataSource = {
    url: '/query/testLog',
    totalProperty: 'count',
    params: {
        type: 'test',
        test: 'UIPerformance',
        metrics: 'testTime,avgloadtime,avgrequests,avgresponsessize,pagename,loadcount',
        trends: 'avgloadtime,maxloadtime,minloadtime',
        searchFields: 'pagename',
    },
};

const useStyles = makeStyles((theme) => ({
    toggleButtonContainer: {
        ...theme.mixins.flex.justifyContentFlexEnd,
    },
    toggle: {
        '& .MuiSvgIcon-root': {
            fontSize: '1rem',
        },
    },
}));

const columns = [
    {
        title: t`Date/Time`,
        width: 220,
        sortable: true,
        mapping: 'testTime',
        formatter: FORMATTER_TYPES.DATETIME,
    },
    {
        type: COLUMN_TYPES.TEXT,
        sortable: true,
        title: t`Page Name`,
        mapping: 'pagename',
        width: 200,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        sortable: true,
        title: t`Load Count`,
        mapping: 'loadcount',
        width: 120,
        formatter: metricDefFormatter({ units: '', multiplier: 0 }),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        sortable: true,
        title: 'Load Time',
        mapping: 'avgloadtime',
        width: 120,
        formatter: metricDefFormatter({ units: 'ms', multiplier: 0 }),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        sortable: true,
        title: 'Requests',
        mapping: 'avgrequests',
        width: 120,
        formatter: metricDefFormatter({ units: '', multiplier: 0 }),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        sortable: true,
        title: 'Bytes',
        mapping: 'avgresponsessize',
        width: 120,
        formatter: metricDefFormatter({ units: '', multiplier: 0 }),
    },
];

const sorters = [{ property: 'testTime', direction: 'desc' }];

const ShowIf = ({ children, cond }) => {
    return <div style={{ display: cond ? null : 'none' }}>{children}</div>;
};

function UiPerformanceTestLogPanel(props) {
    const { id } = props;
    const styles = useStyles();
    const [search, setSearch] = useState('');
    const loader = useQueryLoader(dataSource);

    const viewKey = `${id}_viewTab`;
    const [view, setView] = useState(SessionStorageUtilities.getJSONValue(viewKey, defaultView));

    useEffect(() => {
        SessionStorageUtilities.setJSONValue(viewKey, view);
    }, [viewKey, view]);

    return (
        <>
            <Grid container className={styles.toggleButtonContainer}>
                <ToggleButtonGroup
                    size="small"
                    value={view}
                    className={styles.toggle}
                    onChange={(_ctx, value) => {
                        if (value === null) return;
                        setView(value);
                    }}
                    exclusive
                >
                    <ToggleButton value={TAB_STATES.LINE}>
                        <Tooltip title={<Trans>Overall Performance</Trans>}>
                            <LineChartIcon />
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value={TAB_STATES.BAR}>
                        <Tooltip title={<Trans>Per Page Performance</Trans>}>
                            <BarChartIcon />
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <ShowIf cond={view === TAB_STATES.LINE}>
                <UiPerformanceChart {...props} search={search} />
            </ShowIf>
            <ShowIf cond={view === TAB_STATES.BAR}>
                <UiPerformanceBoxPlot {...props} search={search} />
            </ShowIf>
            <ReportingDataTable
                panelProps={props}
                loader={loader}
                columns={columns}
                visibleRows={0}
                sorters={sorters}
                onStateChanged={(ev) => setSearch(ev.search)}
            />
        </>
    );
}

UiPerformanceTestLogPanel.propTypes = panelPropTypes;

UiPerformanceTestLogPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`UI Performance Test Log`,
};

export default Panel()(UiPerformanceTestLogPanel);
